import { useId, useRef } from 'react';
import { useIntl } from 'react-intl';
import Button from '@rio-cloud/rio-uikit/Button';
import Card from '@rio-cloud/rio-uikit/Card';
import useOnScreen from '@rio-cloud/rio-uikit/hooks/useOnScreen';
import { getSupportedLocale } from '../../configuration/lang/lang';
import { useUserProfile } from '../../configuration/login/loginSlice';
import { gaPush } from '../../configuration/setup/googleAnalytics';
import { useAdvertisementSeen } from '../../hooks/useAdvertisementSeen';
import type { DriverChatCampaign } from '../../hooks/useGetUpsellingCampaign';
import messagesDE from '../translations/de-DE.json';
import messagesEN from '../translations/en-GB.json';

type MessageName =
    | 'translation'
    | 'intro'
    | 'communication.title'
    | 'communication.description'
    | 'orders.title'
    | 'orders.description'
    | 'compact.intro'
    | 'compact.booking'
    | 'cta';

export const DriverChatUpsellingView = ({ campaign }: { campaign: DriverChatCampaign }) => {
    const ref = useRef<HTMLDivElement>(null);
    const inView = useOnScreen(ref);
    const titleId = useId();
    const intl = useIntl();
    const userProfile = useUserProfile();
    const preferredLocale = userProfile?.locale ?? intl.locale;
    const isGerman = preferredLocale.toLowerCase().split(/[-_]/)[0] === 'de';
    const linkLanguage = isGerman ? 'de' : getSupportedLocale(intl.locale).split('-')[0];
    const messages = isGerman ? messagesDE : intl.messages;
    const isCompact = campaign.layout === 'compact';
    const message = (name: MessageName) => {
        const id = `intl-msg:chat.driverChatUpsell.${name}` as const;
        const translation = messages[id];
        const hasTranslation = typeof translation === 'string' && translation.trim();
        return (
            <span lang={hasTranslation ? (isGerman ? 'de' : intl.locale) : 'en'}>
                {hasTranslation ? translation : messagesEN[id]}
            </span>
        );
    };

    useAdvertisementSeen(inView);

    const title = (
        <h1
            id={titleId}
            className={
                isCompact
                    ? 'text-size-h1 text-medium text-color-darkest line-height-125rel text-wrap-balance margin-0'
                    : 'text-size-h2 text-wrap-balance text-color-darkest margin-0'
            }
            style={{
                textTransform: 'none',
                fontWeight: isCompact ? undefined : 400,
                lineHeight: isCompact ? undefined : 'inherit',
            }}
        >
            {isCompact ? 'Driver chat' : 'Driver Chat'}
        </h1>
    );

    const cta = (
        <Button
            as='a'
            href={`https://rio.cloud/${linkLanguage}/marketplace/produkt/order-communication`}
            target='_blank'
            rel='noopener noreferrer'
            bsStyle='primary'
            bsSize='lg'
            block={isCompact}
            className={isCompact ? undefined : 'text-wrap-balance'}
            iconName={isCompact ? undefined : 'arrow-right'}
            iconRight={!isCompact}
            multiline
            onClick={() => {
                gaPush({
                    category: campaign.trackingCategory,
                    action: 'Learn more',
                    label: 'Clicked learn more link in upselling advertisement',
                });
            }}
        >
            {message('cta')}
        </Button>
    );

    return (
        <div
            ref={ref}
            className={`height-100pct overflow-y-auto ${isCompact ? 'bg-lightest padding-25' : 'bg-white padding-x-20 padding-y-25'}`}
        >
            <Card
                className={`max-width-350 margin-auto ${isCompact ? '' : 'border-none'}`}
                padding={isCompact ? 25 : 0}
                shadow={isCompact ? 'subtle' : 'none'}
            >
                <section
                    aria-labelledby={titleId}
                    className={isCompact ? 'display-grid gap-25 text-center' : 'display-flex flex-column gap-25'}
                >
                    <header
                        className={
                            isCompact
                                ? 'display-grid gap-10 justify-content-center'
                                : 'text-center display-flex flex-column gap-10'
                        }
                    >
                        {isCompact ? (
                            <span className='uikit-icon icon-chat text-size-h1 text-color-primary' aria-hidden='true' />
                        ) : (
                            <div className='display-flex flex-column align-items-center gap-20'>
                                <div className='display-flex align-items-center gap-20' aria-hidden='true'>
                                    <span
                                        className='uikit-icon icon-chat text-color-primary'
                                        style={{ fontSize: 80 }}
                                    />
                                    <span
                                        className='uikit-icon icon-arrows-horizontal text-color-dark'
                                        style={{ fontSize: 48 }}
                                    />
                                    <span
                                        className='uikit-icon icon-language text-color-secondary'
                                        style={{ fontSize: 80 }}
                                    />
                                </div>
                                <div className='text-size-20 text-color-dark'>{message('translation')}</div>
                            </div>
                        )}
                        {isCompact ? (
                            title
                        ) : (
                            <div className='text-center max-width-600 margin-auto'>
                                {title}
                                <p className='text-size-16 text-wrap-balance line-height-125rel text-color-dark margin-bottom-0 margin-top-15'>
                                    {message('intro')}
                                </p>
                            </div>
                        )}
                    </header>
                    {isCompact ? (
                        <div className='display-grid gap-25 justify-content-center'>
                            <p className='text-size-18 text-color-dark line-height-125rel text-wrap-balance margin-0'>
                                {message('compact.intro')}
                            </p>
                            <p className='text-size-18 text-color-dark line-height-125rel text-wrap-balance margin-0'>
                                {message('compact.booking')}
                            </p>
                        </div>
                    ) : (
                        <div className='display-flex flex-column gap-15'>
                            <div className='bg-lightest rounded padding-20 display-flex gap-20'>
                                <span
                                    className='uikit-icon icon-chat text-size-h2 text-color-primary'
                                    aria-hidden='true'
                                />
                                <div>
                                    <h2
                                        className='text-size-16 text-wrap-balance text-medium text-color-darkest margin-0'
                                        style={{ lineHeight: 'inherit' }}
                                    >
                                        {message('communication.title')}
                                    </h2>
                                    <p className='text-wrap-balance text-color-dark margin-bottom-0 margin-top-5'>
                                        {message('communication.description')}
                                    </p>
                                </div>
                            </div>
                            <div className='bg-lightest rounded padding-20 display-flex gap-20'>
                                <span
                                    className='uikit-icon icon-delivery text-size-h2 text-color-primary'
                                    aria-hidden='true'
                                />
                                <div>
                                    <h2
                                        className='text-size-16 text-wrap-balance text-medium text-color-darkest margin-0'
                                        style={{ lineHeight: 'inherit' }}
                                    >
                                        {message('orders.title')}
                                    </h2>
                                    <p className='text-wrap-balance text-color-dark margin-bottom-0 margin-top-5'>
                                        {message('orders.description')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {isCompact ? cta : <div className='text-center'>{cta}</div>}
                </section>
            </Card>
        </div>
    );
};
